/* Kkumi password-page waitlist — email capture that works while the whole
   storefront is gated behind Shopify's password page.

   The store's normal signup (assets/section-kkumi-email-signup.js) posts to
   the rewards worker over the App Proxy (/apps/rewards/...) and confirms via
   a storefront landing page. Both of those live behind the password gate, so
   neither is reachable from this page. Instead we talk straight to Klaviyo's
   public *client* API from the browser — a cross-origin call to
   a.klaviyo.com that the password gate never sees:

     POST /client/subscriptions/  → adds the profile to the waitlist list
     POST /client/events/         → fires the waitlist metric (best effort,
                                    so a Klaviyo flow can welcome them)

   PRIMARY PATH is the rewards worker's public relay (`relayUrl`), which calls
   Klaviyo server-side. The browser then only ever talks to a Kkumi domain.
   That matters because a.klaviyo.com is on the common tracker blocklists: a
   filtering VPN, ad blocker or privacy DNS kills a direct call, the request
   dies with no response to read a status from, and the signup is lost with the
   visitor seeing only "network hiccup". Confirmed live, not theorised.

   The direct Klaviyo call is kept as a FALLBACK for the case the relay itself
   is unreachable (worker down, endpoint not deployed yet). Company (public)
   key comes from Theme settings → klaviyo_public_key; the list id is a section
   setting.

   DELIBERATELY NO FALLBACK to /apps/rewards/subscribe-request. That endpoint
   fires `Subscribe Confirmation` and, on confirm, `Kkumi Club Joined` — the
   metrics behind the live Subscribe Confirmation and Welcome Series flows,
   which promise a free colour pop kit and Club perks that don't exist while
   the store is gated. A waitlist signup must reach the waitlist list and
   nothing else, so when the Klaviyo key or list id is missing this form
   errors rather than quietly routing the person into the club funnel.

   Config arrives via <script type="application/json" data-kk-pwp-waitlist-config>. */
(function () {
  var KLAVIYO_BASE = 'https://a.klaviyo.com/client';
  var REVISION = '2024-10-15';
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  /* Bot guards. The waitlist is single opt-in by design (a confirm link can't
     be served from behind the password gate), so nothing downstream filters
     junk addresses — a list of dead addresses is a sending-reputation problem
     on the day this list is finally mailed. Two cheap, captcha-free checks:
     a honeypot field, and a minimum time between the form appearing and the
     submit. Neither is visible to a real person. */
  var MIN_FILL_MS = 2000;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function initInstance(cfg) {
    ready(function () {
      var form = document.getElementById(cfg.formId || '');
      if (!form) return;

      var wrap = form.closest('[data-kk-waitlist]') || form;
      var input = form.querySelector('input[type="email"]');
      var button = form.querySelector('button[type="submit"]');
      // Queried from the wrapper, not the form: the message sits outside the
      // form so the done state can hide the form and still show it.
      var msg = wrap.querySelector('[data-waitlist-msg]');
      var storageKey = 'kkumi_waitlist_joined';
      var honeypot = form.querySelector('[data-waitlist-hp]');
      var readyAt = Date.now();

      /* Async buttons go through window.KKBtn (FRONTEND.md). The section loads
         it explicitly because the password layout skips theme.liquid's globals;
         the fallback keeps the button honest if that ever stops arriving. */
      function setBusy() {
        wrap.classList.add('is-busy');
        if (!button) return;
        if (window.KKBtn) window.KKBtn.busy(button, cfg.busyLabel);
        else { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      }
      function clearBusy() {
        wrap.classList.remove('is-busy');
        if (!button) return;
        if (window.KKBtn) window.KKBtn.reset(button);
        else { button.disabled = false; button.removeAttribute('aria-busy'); }
      }

      function say(text, ok) {
        if (!msg) return;
        msg.hidden = !text;
        msg.textContent = text || '';
        msg.classList.toggle('ok', !!ok);
      }

      /* `remember` is false on the honeypot branch. That branch shows the
         success state having sent nothing, so remembering it would tell a
         person whose browser filled the trap that they had joined — forever,
         on this device. Only a signup we actually sent is worth storing. */
      function done(remember) {
        clearBusy();
        wrap.classList.add('is-done');
        say(cfg.successMessage, true);
        if (!remember) return;
        try { localStorage.setItem(storageKey, '1'); } catch (e) { /* private mode */ }
      }

      /* Returning visitor who already signed up — greet them, but LEAVE THE
         FORM UP.

         This flag is a hint, never a gate. It is per-device, it survives a
         signup that silently failed, and the build live until 2026-08-30
         wrote it whenever mobile autofill tripped the then `company`-named
         honeypot. Hiding the form on the strength of it locked those visitors
         out of the waitlist for good, the page cheerfully telling them they
         were already on a list they had never reached — on exactly the mobile
         traffic this page exists to capture. Renaming the field stopped new
         cases and released none of the existing ones.

         So: nothing stored in this browser may block a signup. The server is
         the one that knows who is on the list, and a repeat subscribe there
         is a harmless no-op. */
      try {
        if (localStorage.getItem(storageKey) === '1') {
          say(cfg.alreadyMessage || cfg.successMessage, true);
        }
      } catch (e) { /* private mode */ }

      function klaviyoSubscribe(email) {
        var payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: cfg.source,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    properties: { signup_source: cfg.source },
                  },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: cfg.listId } } },
          },
        };
        return fetch(KLAVIYO_BASE + '/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: REVISION },
          body: JSON.stringify(payload),
        });
      }

      /* Best effort: a metric event gives the team a flow trigger and a
         countable signal per signup. Never blocks the success state. */
      function klaviyoEvent(email) {
        if (!cfg.eventName) return;
        var payload = {
          data: {
            type: 'event',
            attributes: {
              properties: { source: cfg.source },
              metric: { data: { type: 'metric', attributes: { name: cfg.eventName } } },
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
          },
        };
        fetch(KLAVIYO_BASE + '/events/?company_id=' + encodeURIComponent(cfg.companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: REVISION },
          body: JSON.stringify(payload),
        }).catch(function () { /* non-fatal */ });
      }

      /* The actual send. Split out from the submit handler so the timing
         guard can defer it with a plain setTimeout instead of synthesising a
         second submit event. */
      /* Post to our own worker. Same shape as the Klaviyo call from the page's
         point of view: resolves with a response, rejects only when the request
         never completes. */
      function relaySubscribe(email) {
        return fetch(cfg.relayUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            source: cfg.source,
            // Server-side honeypot: a script that skipped the page still has to
            // guess which field is the trap.
            kkhp: honeypot ? honeypot.value : '',
          }),
        });
      }

      function send(email) {
        setBusy();
        say('', false);

        var canDirect = !!(cfg.companyId && cfg.listId);
        if (!cfg.relayUrl && !canDirect) {
          clearBusy();
          say(cfg.errorGeneric, false);
          console.error('[Kkumi waitlist] no relay URL and no Klaviyo config — refusing to send this signup anywhere else');
          return;
        }

        var attempt = cfg.relayUrl
          ? relaySubscribe(email).catch(function (err) {
              // The relay never answered. Fall back to Klaviyo directly, which
              // still works for the majority who aren't behind a blocker —
              // better than dropping the signup entirely.
              console.error('[Kkumi waitlist] relay unreachable, trying Klaviyo directly', err && err.name);
              if (!canDirect) throw err;
              return klaviyoSubscribe(email).then(function (res) {
                if (res.ok) klaviyoEvent(email);
                return res;
              });
            })
          : klaviyoSubscribe(email).then(function (res) {
              if (res.ok) klaviyoEvent(email);
              return res;
            });

        attempt
          .then(function (res) {
            if (res.ok) {
              // The metric is fired by whichever side did the subscribe: the
              // relay does it server-side, the fallback does it above.
              done(true);
              return;
            }
            clearBusy();
            say(res.status === 429 ? (cfg.errorRateLimited || cfg.errorGeneric) : cfg.errorGeneric, false);
          })
          .catch(function (err) {
            clearBusy();
            // The request never reached Klaviyo: no response to read a status
            // from. In the wild that is nearly always a content blocker or a
            // DNS-level filter killing a.klaviyo.com, which is on the common
            // tracker blocklists. Log it so a real failure can be told apart
            // from a blocked one when someone reports "network hiccup".
            console.error('[Kkumi waitlist] request never completed', err && (err.name + ': ' + err.message));
            say(cfg.errorNetwork || cfg.errorGeneric, false);
          });
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (wrap.classList.contains('is-busy')) return;

        /* Honeypot filled: a bot. Show the success state and send nothing, so
           it can't tell it was caught and retry with the field left blank. */
        if (honeypot && honeypot.value) { done(false); return; }

        var email = (input && input.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          wrap.classList.add('is-invalid');
          say(cfg.errorInvalid, false);
          if (input) input.focus();
          return;
        }
        wrap.classList.remove('is-invalid');

        /* Submitted faster than a person can read the field and type an
           address. Don't reject — a fast paster is real — just hold the
           request back to the threshold, which breaks the naive load-and-post
           bots this is aimed at. The button goes busy immediately so the wait
           reads as normal submission. */
        var waited = Date.now() - readyAt;
        if (waited < MIN_FILL_MS) {
          setBusy();
          setTimeout(function () { clearBusy(); send(email); }, MIN_FILL_MS - waited);
          return;
        }

        send(email);
      });
    });
  }

  document.querySelectorAll('script[data-kk-pwp-waitlist-config]').forEach(function (el) {
    if (el.dataset.kkInit === '1') return;
    el.dataset.kkInit = '1';
    try { initInstance(JSON.parse(el.textContent)); } catch (err) {
      console.error('[Kkumi waitlist] bad config JSON', err);
    }
  });
})();
